import React, { useEffect, useRef } from 'react';
import './FormPanel.css';

/**
 * FormPanel — Reusable slide-out form drawer.
 * Mirrors the HamburgerMenu drawer pattern (right-side, backdrop, escape key, scroll lock).
 *
 * Props:
 *   isOpen (boolean)     — controls visibility
 *   onClose (function)   — called on backdrop click, escape, X, cancel
 *   title (string)       — drawer header text
 *   submitting (boolean) — disables fields + shows spinner on submit
 *   success (boolean)    — shows success overlay (checkmark + message)
 *   error (string|null)  — shows error banner above form
 *   onSubmit (function)  — called when submit button clicked
 *   children             — form fields rendered inside scrollable body
 */
const FormPanel = ({ isOpen, onClose, title, submitting, success, error, onSubmit, children }) => {
  if (!isOpen) return null;

  return (
    <FormPanelDrawer
      title={title}
      submitting={submitting}
      success={success}
      error={error}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      {children}
    </FormPanelDrawer>
  );
};

/**
 * FormPanelDrawer — the actual drawer component.
 * Separated so hooks run unconditionally (not behind the isOpen gate).
 */
const FormPanelDrawer = ({ title, submitting, success, error, onClose, onSubmit, children }) => {
  const drawerRef = useRef(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus close button on mount
  useEffect(() => {
    if (drawerRef.current) {
      const closeBtn = drawerRef.current.querySelector('.fp-close-btn');
      if (closeBtn) closeBtn.focus();
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!submitting && onSubmit) {
      onSubmit();
    }
  };

  return (
    <div className="fp-backdrop" onClick={submitting ? undefined : onClose}>
      <div
        ref={drawerRef}
        className="fp-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="fp-drawer-header">
          <span className="fp-drawer-title">{title}</span>
          <button
            className="fp-close-btn"
            onClick={onClose}
            aria-label="Close form"
            disabled={submitting}
          >
            &times;
          </button>
        </div>

        {/* Success overlay */}
        {success && (
          <div className="fp-success-overlay">
            <div className="fp-success-checkmark" />
            <span className="fp-success-title">Thank you!</span>
            <span className="fp-success-message">
              We've received your submission and will be in touch.
            </span>
          </div>
        )}

        {/* Form body */}
        {!success && (
          <form onSubmit={handleFormSubmit} style={{ display: 'contents' }}>
            <div className={`fp-form-body${submitting ? ' fp-disabled' : ''}`}>
              {error && (
                <div className="fp-error-banner">
                  <span>{error}</span>
                </div>
              )}
              {children}
            </div>

            {/* Footer */}
            <div className="fp-footer">
              <button
                type="submit"
                className="fp-submit-btn"
                disabled={submitting}
              >
                {submitting && <span className="fp-spinner" />}
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
              <button
                type="button"
                className="fp-cancel-btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FormPanel;
